import { Button, Col, Empty, Form, message, Popover, Row, Select } from 'antd';
import parsePhoneNumber from 'libphonenumber-js';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { createReservation, saveReservation } from '../../../services/reservation';
import { createArea, createJobPosition } from '../../../services/general';
import codes from '../../../utils/providerCodes.json';
import countrySettings from '../countrySettings.json';

import { format } from 'rut.js';

const ValidationButtons = ({
  type,
  form,
  patients,
  branchOfficeId,
  reservation,
  fetchReservation,
  paymentMethods,
  companies,
  countryCode,
  disabled,
}) => {
  let navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const validateReservation = () => {
    const reservation = form.getFieldsValue();

    for (const key in reservation) {

      if ((key === 'user_id' && !reservation[key]) || reservation['user_id'] === undefined) {
        showErrorMessage(`${t('reservations.validationButtons.errorApplicant')}`);
        return false;
      }
      if ((key === 'company_id' && !reservation[key]) || reservation['company_id'] === undefined) {
        showErrorMessage(`${t('reservations.validationButtons.errorCompany')}`);
        return false;
      }
      if ((key === 'branch_office_id' && !reservation[key]) || reservation['branch_office_id'] === undefined) {
        showErrorMessage(`${t('reservations.validationButtons.errorBranchOffice')}`);
        return false;
      }
      if ((key === 'reservation_date' && !reservation[key]) || reservation['reservation_date'] === undefined) {
        showErrorMessage(`${t('reservations.validationButtons.errorDate')}`);
        return false;
      }
      if (countrySettings.generalData.contactMethodRequired[countryCode] && ((key === 'contact_method' && !reservation[key]) || reservation['contact_method'] === undefined)) {
        showErrorMessage(`${t('reservations.validationButtons.errorContact')}`);
        return false;
      }
      if ((key === 'total_patients' && !reservation[key])) {
        showErrorMessage(`${t('reservations.validationButtons.errorTotalPatients')}`);
        return false;
      }
      if (countrySettings.generalData.invoiceTo[countryCode] && ((key === 'invoice_company_id' && !reservation[key]) || reservation['invoice_company_id'] === undefined)) {
        showErrorMessage(`${t('reservations.validationButtons.errorInvoiceCompany')}`);
        return false;
      }
    }
    return true;
  };

  const validatePatientsServices = () => {
    let _patients = patients;

    for (let [index, patient] of _patients.entries()) {
      if (patient['prestations'].length === 0) {
        showErrorMessage(`${t('reservations.validationButtons.errorPatientService')} ${index + 1}`);
        return false;
      } else {
        patient.prestations.forEach((prestation) => {
          delete prestation.locate_code;
          delete prestation.description;
          delete prestation.original_sale_amount;
          delete prestation.price_fallback;
          delete prestation.net_amount;
          delete prestation.availableKeywords;
          delete prestation.conditionalExams;
          return prestation;
        })
      }

      if (!patient['block_id']) {
        showErrorMessage(`${t('reservations.validationButtons.errorPatientBlock')} ${index + 1}`);
        return false;
      }

      if (!patient.document_number || patient.document_number === '-') {
        patient.document_type = null;
        patient.document_number = null;
      }

      if (patient['phone'] && patient['phone'].length > 4) {
        let parsedNumber;

        if (patient['phone'].startsWith('+')) {
          parsedNumber = parsePhoneNumber(patient['phone']);
        }
        else {
          parsedNumber = parsePhoneNumber(`+${patient['phone']}`);
        }
        if (parsedNumber) {
          const checkvalidity = parsedNumber.isPossible();
          if (!checkvalidity) {
            showErrorMessage(`${t('reservations.validationButtons.errorPatientPhone')} ${index + 1}`);
            return false;
          }
        } else {
          showErrorMessage(`${t('reservations.validationButtons.errorPatientPhone')} ${index + 1}`);
          return false;
        }
      }

      if (countrySettings.validatePreadmission[countryCode]) {
        const missingDocument = !patient.document_type || !patient.document_number;
        const missingNames = countrySettings.patientValidation.requireNames[countryCode]
          ? (!patient.names || !patient.last_name) : false;
        const missingGender = countrySettings.patientValidation.requireGender[countryCode]
          ? [undefined, null].includes(patient.gender) : false;
        const missingJobPosition = countrySettings.patientValidation.requireJobPosition[countryCode]
          ? !patient.job_position_id : false;
        const missingArea = countrySettings.patientValidation.requireArea[countryCode]
          ? !patient.area_id : false;
        const missingEvaluationType = [undefined, null, 0].includes(patient.evaluation_type);

        if (missingDocument || missingEvaluationType || missingNames || missingGender || missingJobPosition || missingArea) {
          showErrorMessage(t('reservations.validationButtons.mustFillPatient'));
          return false;
        }
      }
    }

    return true;
  };

  const showErrorMessage = (text) => {
    message.error({
      content: text,
      style: {
        marginTop: '32vh',
        fontSize: 16,
      },
    });
  };

  const validationAndSend = (action) => {
    window.dispatchEvent(new Event('submitPatientFormPE'));
    
    if (!validateReservation() || !validatePatientsServices()) {
      return null;
    } else {
      if (action === 'confirm' || action === 'reconfirm') {
        doConfirmReservation(action);
      }
      if (action === 'save') {
        doSaveReservation();
      }
      if (action === 'create') {
        doCreateReservation();
      }
    }
  };

  const doConfirmReservation = async (action) => {
    const reservationConst = { ...form.getFieldsValue(), company_updated: reservation.has_admissions };

    try {
      setLoading(true);
      let _reservation = {
        ...reservationConst,
        'patients': patients?.map((patient) => {
          const cleanPatient = { ...patient };
          delete cleanPatient.project_name;
          delete cleanPatient._first_last_name;
          delete cleanPatient._second_last_name;

          if (cleanPatient.prestations) {
            cleanPatient.prestations = cleanPatient.prestations.map(prestation => {
              const cleanPrestation = { ...prestation };
              delete cleanPrestation.observations;
              delete cleanPrestation._originalAdditionalIds;
              return cleanPrestation;
            });
          }

          return cleanPatient;
        }),
      };

      if (_reservation['cost_center_id'] === undefined) {
        _reservation = { ..._reservation, 'cost_center_id': null, 'cost_center_name': null };
      }

      if (_reservation['child_company_id'] === undefined) {
        _reservation = { ..._reservation, 'child_company_id': null, 'child_company_name': null };
      }

      _reservation = {
        ..._reservation,
        action,
        ...(action === 'confirm' && { state: 'confirmed' })
      };

      for (let patient of _reservation['patients']) {
        if ((patient['phone'] && patient['phone'].length <= 4) || !patient['phone']) {
          patient['phone'] = null;
        } else if (patient['phone'].startsWith('+')) {
          patient['phone'] = `${patient['phone']}`;
        } else {
          patient['phone'] = `+${patient['phone']}`;
        }
        if (patient.document_number && patient.document_type) {
          patient.document_number = patient.document_type === 'rut' ? format(patient.document_number, { dots: false }) : patient.document_number
        }

        if (typeof patient['job_position_id'] === 'string' && isNaN(patient['job_position_id'])) {
          try {
            const newJobPosition = await createJobPosition({ name: patient['job_position_id'], company_id: reservationConst.company_id });
            patient['job_position_id'] = newJobPosition.id;
          } catch (error) {
            console.error("Failed to create job position", error);
            patient['profession'] = patient['job_position_id'];
            patient['job_position_id'] = null;
          }
        }

      }

      const response = await saveReservation(_reservation);

      if (response) {
        message.success({
          content: `${t('reservations.validationButtons.reservation')} ${action === 'confirmed' ? t('reservations.validationButtons.confirmMessage') : 'reconfirmada'} exitosamente`,
          style: {
            marginTop: '32vh',
            fontSize: 16
          }
        });
        navigate(`/reservas/${reservation.id}/ver`);
      } else {
        message.error({
          content: `Ocurrió un error. Inténtalo nuevamente`,
          style: {
            marginTop: '32vh',
            fontSize: 16
          }
        });
      }
    } catch (error) {
      console.error(error);
      let errorMessage = 'Error al guardar la reserva.'
      if (error.includes('{{documentNumber}}')) {
        errorMessage = error.replace('{{documentNumber}}', patients[0].document_number);
      }
      message.error({
        content: errorMessage,
        style: {
          marginTop: '32vh',
          fontSize: 16
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const doSaveReservation = async () => {
    const reservationConst = { ...form.getFieldsValue(), company_updated: reservation.has_admissions };
    try {
      setLoading(true);
      let _reservation = {
        ...reservationConst,
        'patients': patients?.map((patient) => {
          const cleanPatient = { ...patient };
          delete cleanPatient.project_name;
          delete cleanPatient._first_last_name;
          delete cleanPatient._second_last_name;

          if (cleanPatient.prestations) {
            cleanPatient.prestations = cleanPatient.prestations.map(prestation => {
              const cleanPrestation = { ...prestation };
              delete cleanPrestation.observations;
              delete cleanPrestation._originalAdditionalIds;
              return cleanPrestation;
            });
          }

          return cleanPatient;
        }),
      };

      if (_reservation['cost_center_id'] === undefined) {
        _reservation = { ..._reservation, 'cost_center_id': null, 'cost_center_name': null };
      }

      if (_reservation['child_company_id'] === undefined) {
        _reservation = { ..._reservation, 'child_company_id': null, 'child_company_name': null };
      }

      _reservation['reservation_date'] = _reservation['reservation_date'].format('YYYY-MM-DD');


      for (let patient of _reservation['patients']) {
        if ((patient['phone'] && patient['phone'].length <= 4) || !patient['phone']) {
          patient['phone'] = null;
        } else if (patient['phone'].startsWith('+')) {
          patient['phone'] = `${patient['phone']}`;
        } else {
          patient['phone'] = `+${patient['phone']}`;
        }
        if (patient.document_number && patient.document_type) {
          patient.document_number = patient.document_type === 'rut' ? format(patient.document_number, { dots: false }) : patient.document_number
        }

        if (typeof patient['job_position_id'] === 'string' && isNaN(patient['job_position_id'])) {
          try {
            const newJobPosition = await createJobPosition({ name: patient['job_position_id'], company_id: reservationConst.company_id });
            patient['job_position_id'] = newJobPosition.id;
          } catch (error) {
            console.error("Failed to create job position", error);
            patient['profession'] = patient['job_position_id'];
            patient['job_position_id'] = null;
          }
        }
      }

      const response = await saveReservation(_reservation);

      if (response) {
        message.success({
          content: `${t('reservations.validationButtons.saveMessage')}`,
          style: {
            marginTop: '32vh',
            fontSize: 16
          }
        });
        setLoading(false);
        navigate(`/reservas/${reservationConst.id}/ver`);

      } else {
        message.error({
          content: `Ocurrió un error. Inténtalo nuevamente`,
          style: {
            marginTop: '32vh',
            fontSize: 16
          }
        });
        setLoading(false);
      }
    } catch (error) {
      console.error(error);
      let errorMessage = 'Error al guardar la reserva.'
      if (error.includes('{{documentNumber}}')) {
        errorMessage = error.replace('{{documentNumber}}', patients[0].document_number);
      }
      message.error({
        content: errorMessage,
        style: {
          marginTop: '32vh',
          fontSize: 16
        }
      });
      setLoading(false);
    }
  };

  const doCreateReservation = async () => {
    const reservationConst = form.getFieldsValue();

    try {
      setLoading(true);
      let _reservation = {
        ...reservationConst,
        'patients': patients?.map((patient) => {
          const cleanPatient = { ...patient };
          delete cleanPatient.project_name;
          delete cleanPatient._first_last_name;
          delete cleanPatient._second_last_name;

          if (cleanPatient.prestations) {
            cleanPatient.prestations = cleanPatient.prestations.map(prestation => {
              const cleanPrestation = { ...prestation };
              delete cleanPrestation.observations;
              delete cleanPrestation._originalAdditionalIds;
              return cleanPrestation;
            });
          }

          return cleanPatient;
        }),
      };

      if (_reservation['cost_center_id'] === undefined) {
        _reservation = { ..._reservation, 'cost_center_id': null }
      }

      if (_reservation['child_company_id'] === undefined) {
        _reservation = { ..._reservation, 'child_company_id': null };
      }

      _reservation['reservation_date'] = _reservation['reservation_date'].format('YYYY-MM-DD');

      if (countryCode === 'PE') {
        for (let patient of _reservation['patients']) {
          if ((patient['phone'] && patient['phone'].length <= 4) || !patient['phone']) {
            patient['phone'] = null;
          } else if (patient['phone'].startsWith('+')) {
            patient['phone'] = `${patient['phone']}`;
          } else {
            patient['phone'] = `+${patient['phone']}`;
          }
        }
      }

      for (let patient of _reservation['patients']) {
        if (typeof patient['job_position_id'] === 'string' && isNaN(patient['job_position_id'])) {
          try {
            const newJobPosition = await createJobPosition({ name: patient['job_position_id'], company_id: reservationConst.company_id });
            patient['job_position_id'] = newJobPosition.id;
          } catch (error) {
            console.error("Failed to create job position", error);
            patient['profession'] = patient['job_position_id'];
            patient['job_position_id'] = null;
          }
        }
      }

      const response = await createReservation(_reservation, branchOfficeId);

      if (response && response.id) {
        message.success({
          content: `${t('reservations.validationButtons.createMessage')}`,
          style: {
            marginTop: '32vh',
            fontSize: 16
          }
        });
        setLoading(false);
        navigate(`/reservas/${response.id}/ver`);

      } else {
        message.error({
          content: `Ocurrió un error. Inténtalo nuevamente`,
          style: {
            marginTop: '32vh',
            fontSize: 16
          }
        });
        setLoading(false);
      }
    } catch (error) {
      console.error(error);
      let errorMessage = 'Error al guardar la reserva.';
      if (error.includes('{{documentNumber}}')) {
        errorMessage = error.replace('{{documentNumber}}', patients[0].document_number);
      }
      message.error({
        content: errorMessage,
        style: {
          marginTop: '32vh',
          fontSize: 16
        }
      });
      setLoading(false);
    }
  };

  const handleChangePayment = async (value) => {
    await paymentMethods.doUpdate({
      reservation_id: reservation.id,
      payment_method_id: value,
    })
    fetchReservation();
  };

  return (
    <Row justify="center" className="reservation-settings-wrapper__reservation-buttons mt-70">
      <Col lg={22} xs={22}>
        {
          !type && reservation && reservation.deleted_at === null &&
          <Row justify='space-between'>
            <Col lg={10} xs={24}>
              {
                (paymentMethods && paymentMethods.paymentData && !reservation.payment_methods_codes.some(payment_method =>
                  [codes.convenio, codes.convenioOC].includes(payment_method)
                ) && !reservation.paid) &&
                <Row className='mb-lg-0 mb-md-20 mb-sm-20 mb-xs-20'>
                  <Col lg={10} xs={24}>
                    <p className='mt-5'>Cambiar método de pago</p>
                  </Col>
                  <Col lg={12} xs={24}>
                    <Form.Item>
                      <Select
                        className='cmt-select'
                        size='large'
                        onChange={handleChangePayment}
                        loading={paymentMethods.paymentsLoading}
                        disabled={paymentMethods.paymentsLoading}
                        defaultValue={reservation.payment_methods[0] || null}
                        placeholder='Seleccione un método'
                        block
                        options={
                          (
                            paymentMethods.paymentData.some(payment => payment.default_method)
                              ? paymentMethods.paymentData.filter(payment => payment.default_method)
                              : paymentMethods.paymentData.filter(payment => payment.code && ![codes.convenio, codes.convenioOC].includes(payment.code))
                          ).map(payment => ({ value: payment.id, label: payment.name }))
                        }
                        notFoundContent={
                          <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                              <p className="text-center">
                                No hay métodos de pago
                              </p>
                            }
                          />
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>
              }
            </Col>
            <Col lg={9} xs={24}>
              <Row gutter={10} justify='space-between'>
                <Col xl={12} lg={12} xs={24}>
                  {
                    (!(reservation.payment_methods_codes && reservation.payment_methods_codes.includes(codes.mercadopagocl))
                      && (form.getFieldValue('state') === 'drafted') ?
                      <>
                        {
                          !(companies && companies.length > 0 && companies[0].isComplete) ?
                            <Popover
                              trigger="hover"
                              overlayClassName="sidebar-tooltip"
                              placement="topLeft"
                              content={
                                <p className="text-center mb-0">Debe completar la informaci&oacute;n de la empresa</p>
                              }
                            >
                              <Button
                                loading={loading}
                                onClick={() => validationAndSend('confirm')}
                                className='cmt-button primary disabled'
                                type="primary"
                                size="large"
                                disabled={disabled?.patientBlocking}
                              >
                                {t('reservations.validationButtons.confirmButton')}
                              </Button>
                            </Popover>
                            :
                            <Button
                              loading={loading}
                              onClick={() => validationAndSend('confirm')}
                              className={`cmt-button primary mb-15 ${disabled?.patientBlocking ? 'disabled' : ''}`}
                              type="primary"
                              size="large"
                              disabled
                            >
                              {t('reservations.validationButtons.confirmButton')}
                            </Button>
                        }
                      </>
                      :
                      <>
                        {
                          (!(companies && companies.length > 0 && companies[0].iscomplete)) ?
                            <Popover
                              trigger="hover"
                              overlayClassName="sidebar-tooltip"
                              placement="topLeft"
                              content={
                                <p className="text-center mb-0">Debe completar la informaci&oacute;n de la empresa</p>
                              }
                            >
                              <Button
                                loading={loading}
                                onClick={() => validationAndSend('reconfirm')}
                                className="cmt-button primary mb-15 disabled"
                                type="primary"
                                size="large"
                                disabled={disabled?.patientBlocking}
                              >
                                {t('reservations.validationButtons.reConfirmButton')}
                              </Button>
                            </Popover>
                            :
                            <Button
                              loading={loading}
                              onClick={() => validationAndSend('reconfirm')}
                              className={`cmt-button primary mb-15 ${disabled?.patientBlocking ? 'disabled' : ''}`}
                              type="primary"
                              size="large"
                              disabled
                            >
                              {t('reservations.validationButtons.reConfirmButton')}
                            </Button>
                        }
                      </>)
                  }
                </Col>
                <Col xl={12} lg={12} xs={24}>
                  <Button
                    loading={loading}
                    onClick={() => validationAndSend('save')}
                    className={`cmt-button primary outline mb-15 ${disabled?.patientBlocking ? 'disabled' : ''}`}
                    type="primary"
                    size="large"
                    disabled={disabled?.patientBlocking}
                  >
                    {t('reservations.validationButtons.saveButton')}
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        }
        {
          (type && type === 'creation') &&
          <Row justify="end">
            <Col xl={5} lg={6} xs={24}>
              <Button
                loading={loading}
                onClick={() => validationAndSend('create')}
                className={`cmt-button primary mr-20 ${disabled?.patientBlocking ? 'disabled' : ''}`}
                type="primary"
                size="large"
                disabled={disabled?.patientBlocking}
              >
                {t('reservations.validationButtons.createButton')}
              </Button>
            </Col>
          </Row>
        }
      </Col >
    </Row >
  );
};

export default ValidationButtons;
